import React, { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  Link2,
  Search,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useUserPermissions } from "@/store/useUserPermissions";
import { isoDateToMDY } from "@/utils/common";
import { hasAnyPolicy } from "@/utils/policies";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabToolbar } from "../shared/components/TabToolbar";

const GET_REFERENCE_BROWSER_DATA = gql`
  query GetReferenceBrowserData($odrnumber: Int!, $odrString: String!, $includeCoordinatedCare: Boolean!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      id
      name {
        firstName
        lastName
      }
      programDetails {
        programDescription
        active
      }
      bookingDetails {
        bookingNumber
        arrestDate
        releaseDate
        cases {
          caseNumber
          charges {
            charge
          }
          hearings {
            hearingDate
            details
            type
          }
        }
      }
      notes {
        note
        date
        author
        program
      }
      keyEvents {
        uniqueid
        startdate
        enddate
        event
        notes
        entryby
        entrydate
      }
      documents {
        id
        type
        date
        tags
      }
      scans {
        id
        filedate
        filelabel
        filetype
      }
      incidents {
        id
        incidentDate
        reportCategory
        incidentLocationName
        program
        loginName
      }
      outreach {
        id
        contactDate
        contactMethod
        results
        qaStaff
        nameOfContact
        notes
      }
    }
    coordinatedCareNotes(odrnumber: $odrString) @include(if: $includeCoordinatedCare) {
      id
      noteEntryDate
      procedurenote
      serviceType
      entryby
    }
  }
`;

type EntityType =
  | "participant"
  | "booking"
  | "case"
  | "hearing"
  | "note"
  | "coordinated_care"
  | "key_event"
  | "document"
  | "scan"
  | "incident"
  | "outreach";

interface ReferenceEntity {
  key: string;
  type: EntityType;
  title: string;
  subtitle: string;
  dateLabel?: string;
  metadata: string[];
  dependencies: string[];
  searchBlob: string;
}

interface ReferenceBrowserTabProps {
  odrnumber: number;
}

interface QueryData {
  getParticipantByODRNumber: {
    id: number;
    name?: {
      firstName?: string;
      lastName?: string;
    };
    programDetails?: Array<{
      programDescription?: string;
      active?: boolean;
    }>;
    bookingDetails?: Array<{
      bookingNumber?: number;
      arrestDate?: string;
      releaseDate?: string;
      cases?: Array<{
        caseNumber?: string;
        charges?: Array<{ charge?: string }>;
        hearings?: Array<{
          hearingDate?: string;
          details?: string;
          type?: string;
        }>;
      }>;
    }>;
    notes?: Array<{
      note?: string;
      date?: string;
      author?: string;
      program?: string;
    }>;
    keyEvents?: Array<{
      uniqueid?: number;
      startdate?: string;
      enddate?: string;
      event?: string;
      notes?: string;
      entryby?: string;
      entrydate?: string;
    }>;
    documents?: Array<{
      id?: number;
      type?: string;
      date?: string;
      tags?: string[];
    }>;
    scans?: Array<{
      id?: number;
      filedate?: string;
      filelabel?: string;
      filetype?: string;
    }>;
    incidents?: Array<{
      id?: number;
      incidentDate?: string;
      reportCategory?: string;
      incidentLocationName?: string;
      program?: string;
      loginName?: string;
    }>;
    outreach?: Array<{
      id?: number;
      contactDate?: string;
      contactMethod?: number;
      results?: number;
      qaStaff?: string;
      nameOfContact?: string;
      notes?: string;
    }>;
  };
  coordinatedCareNotes?: Array<{
    id?: number;
    noteEntryDate?: string;
    procedurenote?: string;
    serviceType?: string;
    entryby?: string;
  }>;
}

const ENTITY_LABELS: Record<EntityType, string> = {
  participant: "Participant",
  booking: "Booking",
  case: "Case",
  hearing: "Hearing",
  note: "General Note",
  coordinated_care: "Care Note",
  key_event: "Key Event",
  document: "Document",
  scan: "Scan",
  incident: "Incident",
  outreach: "Outreach",
};

const ENTITY_BADGE_STYLE: Record<EntityType, string> = {
  participant: "bg-slate-50 text-slate-700 border-slate-200",
  booking: "bg-amber-50 text-amber-700 border-amber-200",
  case: "bg-amber-50 text-amber-700 border-amber-200",
  hearing: "bg-yellow-50 text-yellow-700 border-yellow-200",
  note: "bg-blue-50 text-blue-700 border-blue-200",
  coordinated_care: "bg-purple-50 text-purple-700 border-purple-200",
  key_event: "bg-indigo-50 text-indigo-700 border-indigo-200",
  document: "bg-cyan-50 text-cyan-700 border-cyan-200",
  scan: "bg-teal-50 text-teal-700 border-teal-200",
  incident: "bg-red-50 text-red-700 border-red-200",
  outreach: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const toDateValue = (value?: string) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return 0;
  return parsed;
};

export const ReferenceBrowserTab: React.FC<ReferenceBrowserTabProps> = ({ odrnumber }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const { policies } = useUserPermissions();
  const includeCoordinatedCare = hasAnyPolicy(policies, [
    "program:participant:coordinatedcare~full",
  ]);

  const { data, loading, error } = useQuery<QueryData>(GET_REFERENCE_BROWSER_DATA, {
    variables: {
      odrnumber,
      odrString: String(odrnumber),
      includeCoordinatedCare,
    },
    skip: !odrnumber,
  });

  useEffect(() => {
    setSelectedKey(null);
  }, [odrnumber]);

  const entities = useMemo(() => {
    if (!data?.getParticipantByODRNumber) return [] as ReferenceEntity[];

    const participant = data.getParticipantByODRNumber;
    const participantDisplayName = [participant.name?.firstName, participant.name?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const rootKey = `participant:${odrnumber}`;
    const nextEntities: ReferenceEntity[] = [];

    const pushEntity = (entity: ReferenceEntity) => {
      nextEntities.push(entity);
    };

    const activePrograms = (participant.programDetails || [])
      .filter((program) => program.active)
      .map((program) => program.programDescription)
      .filter(Boolean) as string[];

    pushEntity({
      key: rootKey,
      type: "participant",
      title: participantDisplayName || `Participant ${odrnumber}`,
      subtitle: `ODR ${odrnumber}`,
      metadata: activePrograms.length > 0 ? activePrograms : ["Participant Root"],
      dependencies: [],
      searchBlob: `${participantDisplayName} ${odrnumber} ${activePrograms.join(" ")}`.toLowerCase(),
    });

    (participant.bookingDetails || []).forEach((booking, bookingIndex) => {
      const bookingNumber = booking.bookingNumber ? String(booking.bookingNumber) : `unknown-${bookingIndex}`;
      const bookingKey = `booking:${bookingNumber}`;
      const bookingMeta = [
        booking.arrestDate ? `Arrest ${isoDateToMDY(booking.arrestDate)}` : "",
        booking.releaseDate ? `Release ${isoDateToMDY(booking.releaseDate)}` : "",
      ].filter(Boolean);

      pushEntity({
        key: bookingKey,
        type: "booking",
        title: `Booking ${bookingNumber}`,
        subtitle: "Legal booking record",
        dateLabel: booking.arrestDate || booking.releaseDate || undefined,
        metadata: bookingMeta.length > 0 ? bookingMeta : ["No booking dates"],
        dependencies: [rootKey],
        searchBlob: `booking ${bookingNumber} ${bookingMeta.join(" ")}`.toLowerCase(),
      });

      (booking.cases || []).forEach((caseRecord, caseIndex) => {
        const caseNumber = caseRecord.caseNumber || `case-${bookingNumber}-${caseIndex + 1}`;
        const caseKey = `case:${bookingNumber}:${caseNumber}`;
        const chargeText = (caseRecord.charges || [])
          .map((chargeRecord) => chargeRecord.charge || "")
          .filter(Boolean)
          .join(" | ");

        pushEntity({
          key: caseKey,
          type: "case",
          title: `Case ${caseNumber}`,
          subtitle: `Booking ${bookingNumber}`,
          metadata: chargeText ? [chargeText] : ["No charges listed"],
          dependencies: [bookingKey, rootKey],
          searchBlob: `case ${caseNumber} ${bookingNumber} ${chargeText}`.toLowerCase(),
        });

        (caseRecord.hearings || []).forEach((hearing, hearingIndex) => {
          const hearingKey = `hearing:${bookingNumber}:${caseNumber}:${hearingIndex + 1}`;
          const hearingDetails = [hearing.type, hearing.details].filter(Boolean).join(" - ");

          pushEntity({
            key: hearingKey,
            type: "hearing",
            title: hearing.type || "Hearing",
            subtitle: `Case ${caseNumber}`,
            dateLabel: hearing.hearingDate || undefined,
            metadata: hearingDetails ? [hearingDetails] : ["No hearing details"],
            dependencies: [caseKey, bookingKey, rootKey],
            searchBlob: `hearing ${caseNumber} ${hearing.type || ""} ${hearing.details || ""}`.toLowerCase(),
          });
        });
      });
    });

    (participant.notes || []).forEach((note, index) => {
      const noteKey = `note:${index + 1}`;
      const noteText = note.note || "";
      pushEntity({
        key: noteKey,
        type: "note",
        title: `General Note ${index + 1}`,
        subtitle: noteText || "No note text",
        dateLabel: note.date || undefined,
        metadata: [note.author || "Unknown author", note.program || ""].filter(Boolean),
        dependencies: [rootKey],
        searchBlob: `${noteKey} ${noteText} ${note.author || ""} ${note.program || ""}`.toLowerCase(),
      });
    });

    (data.coordinatedCareNotes || []).forEach((careNote, index) => {
      const careKey = `care:${careNote.id || index + 1}`;
      const careText = careNote.procedurenote || "";
      pushEntity({
        key: careKey,
        type: "coordinated_care",
        title: `Care Note ${careNote.id || index + 1}`,
        subtitle: careText || "No note text",
        dateLabel: careNote.noteEntryDate || undefined,
        metadata: [careNote.serviceType || "", careNote.entryby || ""].filter(Boolean),
        dependencies: [rootKey],
        searchBlob: `${careKey} ${careText} ${careNote.serviceType || ""} ${careNote.entryby || ""}`.toLowerCase(),
      });
    });

    (participant.keyEvents || []).forEach((eventRecord, index) => {
      const eventKey = `key-event:${eventRecord.uniqueid || index + 1}`;
      pushEntity({
        key: eventKey,
        type: "key_event",
        title: eventRecord.event || `Key Event ${eventRecord.uniqueid || index + 1}`,
        subtitle: eventRecord.notes || "No event notes",
        dateLabel: eventRecord.startdate || eventRecord.entrydate || undefined,
        metadata: [eventRecord.entryby || "", eventRecord.enddate ? `End ${isoDateToMDY(eventRecord.enddate)}` : ""].filter(Boolean),
        dependencies: [rootKey],
        searchBlob: `${eventKey} ${eventRecord.event || ""} ${eventRecord.notes || ""} ${eventRecord.entryby || ""}`.toLowerCase(),
      });
    });

    (participant.documents || []).forEach((documentRecord) => {
      if (!documentRecord.id) return;
      const documentKey = `document:${documentRecord.id}`;
      const tagText = (documentRecord.tags || []).join(" ");
      pushEntity({
        key: documentKey,
        type: "document",
        title: documentRecord.type || `Document ${documentRecord.id}`,
        subtitle: `Document ID ${documentRecord.id}`,
        dateLabel: documentRecord.date || undefined,
        metadata: documentRecord.tags && documentRecord.tags.length > 0 ? documentRecord.tags : ["No tags"],
        dependencies: [rootKey],
        searchBlob: `${documentKey} ${documentRecord.type || ""} ${tagText}`.toLowerCase(),
      });
    });

    (participant.scans || []).forEach((scanRecord) => {
      if (!scanRecord.id) return;
      const scanKey = `scan:${scanRecord.id}`;
      pushEntity({
        key: scanKey,
        type: "scan",
        title: scanRecord.filelabel || scanRecord.filetype || `Scan ${scanRecord.id}`,
        subtitle: `Scan ID ${scanRecord.id}`,
        dateLabel: scanRecord.filedate || undefined,
        metadata: [scanRecord.filetype || "Unknown type"],
        dependencies: [rootKey],
        searchBlob: `${scanKey} ${scanRecord.filelabel || ""} ${scanRecord.filetype || ""}`.toLowerCase(),
      });
    });

    (participant.incidents || []).forEach((incidentRecord) => {
      if (!incidentRecord.id) return;
      const incidentKey = `incident:${incidentRecord.id}`;
      const incidentText = [incidentRecord.reportCategory, incidentRecord.incidentLocationName]
        .filter(Boolean)
        .join(" - ");
      pushEntity({
        key: incidentKey,
        type: "incident",
        title: `Incident ${incidentRecord.id}`,
        subtitle: incidentText || "Incident record",
        dateLabel: incidentRecord.incidentDate || undefined,
        metadata: [incidentRecord.program || "", incidentRecord.loginName || ""].filter(Boolean),
        dependencies: [rootKey],
        searchBlob: `${incidentKey} ${incidentText} ${incidentRecord.program || ""} ${incidentRecord.loginName || ""}`.toLowerCase(),
      });
    });

    (participant.outreach || []).forEach((outreachRecord) => {
      if (!outreachRecord.id) return;
      const outreachKey = `outreach:${outreachRecord.id}`;
      const outreachText = [outreachRecord.qaStaff, outreachRecord.nameOfContact]
        .filter(Boolean)
        .join(" - ");
      pushEntity({
        key: outreachKey,
        type: "outreach",
        title: `Outreach ${outreachRecord.id}`,
        subtitle: outreachRecord.notes || outreachText || "Outreach attempt",
        dateLabel: outreachRecord.contactDate || undefined,
        metadata: [outreachRecord.qaStaff || "", outreachRecord.nameOfContact || ""].filter(Boolean),
        dependencies: [rootKey],
        searchBlob: `${outreachKey} ${outreachRecord.notes || ""} ${outreachText}`.toLowerCase(),
      });
    });

    return nextEntities.sort((a, b) => toDateValue(b.dateLabel) - toDateValue(a.dateLabel));
  }, [data, odrnumber]);

  const entityMap = useMemo(() => {
    const map: Record<string, ReferenceEntity> = {};
    entities.forEach((entity) => {
      map[entity.key] = entity;
    });
    return map;
  }, [entities]);

  const filteredEntities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = entities.filter((entity) => {
      if (!term) return true;
      return entity.searchBlob.includes(term);
    });
    return filtered.sort((a, b) => toDateValue(b.dateLabel) - toDateValue(a.dateLabel));
  }, [entities, searchTerm]);

  useEffect(() => {
    if (filteredEntities.length === 0) {
      setSelectedKey(null);
      return;
    }

    if (selectedKey && filteredEntities.some((entity) => entity.key === selectedKey)) {
      return;
    }

    setSelectedKey(filteredEntities[0].key);
  }, [filteredEntities, selectedKey]);

  const selectedEntity = selectedKey ? entityMap[selectedKey] : null;

  const selectEntity = (key: string) => {
    setSelectedKey(key);
  };

  if (loading) return <TabSpinner label="Loading reference browser..." />;

  if (error) {
    return (
      <div className="p-12">
        <EmptyState
          icon={Search}
          title="Unable to load references"
          description={error.message}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <TabHeader
        icon={Search}
        title="Reference Browser"
        subtitle="Search entities and inspect participant record details"
      />

      <TabToolbar
        left={(
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search IDs, names, labels, tags, notes..."
              className="h-8 w-[360px] pl-8 text-xs bg-gray-50/50 border-gray-200 focus-visible:ring-1"
            />
          </div>
        )}
        right={<span className="text-xs text-gray-500">{filteredEntities.length} entities</span>}
      />

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[1.25fr,1fr]">
        <Card className="min-h-0 min-w-0 border-gray-200 shadow-sm">
          <CardContent className="h-full overflow-y-auto p-0">
            {filteredEntities.length === 0 && (
              <div className="p-8">
                <EmptyState
                  icon={Search}
                  title="No references match this search"
                  description="Try a broader term or clear filters."
                />
              </div>
            )}

            {filteredEntities.map((entity) => {
              const isSelected = selectedKey === entity.key;
              return (
                <div
                  key={entity.key}
                  className={cn(
                    "border-b border-gray-100 px-3 py-2 cursor-pointer hover:bg-gray-50",
                    isSelected && "bg-blue-50/60"
                  )}
                  onClick={() => selectEntity(entity.key)}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-semibold text-gray-900">{entity.title}</div>
                        <Badge variant="outline" className={cn("h-5 px-1.5 text-xs font-bold", ENTITY_BADGE_STYLE[entity.type])}>
                          {ENTITY_LABELS[entity.type]}
                        </Badge>
                      </div>
                      <div className="truncate text-xs text-gray-600">{entity.subtitle}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {entity.metadata.slice(0, 2).map((meta) => (
                          <span key={meta} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                            {meta}
                          </span>
                        ))}
                        {entity.dateLabel && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                            {isoDateToMDY(entity.dateLabel)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="min-h-0 min-w-0 max-w-full border-gray-200 shadow-sm">
          <CardContent className="h-full max-w-full overflow-x-hidden overflow-y-auto p-4">
            {!selectedEntity && (
              <EmptyState
                icon={Link2}
                title="Select an entity"
                description="Choose an item from the results to inspect its details."
              />
            )}

            {selectedEntity && (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 max-w-full flex-1">
                    <div className="max-w-[60ch] break-words text-base font-semibold text-gray-900 [overflow-wrap:anywhere]">
                      {selectedEntity.title}
                    </div>
                    <div className="max-w-[60ch] break-words text-xs text-gray-500 [overflow-wrap:anywhere]">
                      {selectedEntity.subtitle}
                    </div>
                  </div>
                  <Badge variant="outline" className={cn("h-5 px-1.5 text-xs font-bold", ENTITY_BADGE_STYLE[selectedEntity.type])}>
                    {ENTITY_LABELS[selectedEntity.type]}
                  </Badge>
                </div>

                <div>
                  <div className="mb-1 text-xs font-bold uppercase tracking-[0.08em] text-gray-500">Metadata</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedEntity.metadata.length === 0 && (
                      <span className="text-xs text-gray-500">No metadata</span>
                    )}
                    {selectedEntity.metadata.map((meta) => (
                      <span
                        key={meta}
                        className="inline-flex max-w-[20rem] break-words rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 [overflow-wrap:anywhere]"
                      >
                        {meta}
                      </span>
                    ))}
                    {selectedEntity.dateLabel && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                        Date {isoDateToMDY(selectedEntity.dateLabel)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
